import axios from 'axios';

export const BASE_URL = 'https://api.fcreation.in/api';

const api = axios.create({ baseURL: BASE_URL });

// ── Products ──────────────────────────────────────────────
export const fetchProducts = async () => {
  const { data } = await api.get('/products');
  return data.data;
};

// ── Categories ────────────────────────────────────────────
export const fetchCategories = async () => {
  const { data } = await api.get('/categories');
  return data.data;
};

// ── Single product ─────────────────────────────────────────
export const fetchProductById = async (id) => {
  const { data } = await api.get(`/products/${id}`);
  return data.data;
};

// ── Helpers: normalise API product → component-friendly shape ──
export const normaliseProduct = (p) => {
  const base = parseFloat(p.base_price || 0);
  const sale = parseFloat(p.discount_price || base);

  // Build ordered media list keeping the video flag, then derive the
  // legacy flat `images` string array from it for backward compat.
  const media = (p.product_images || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((img) => ({
      url: img.image_url,
      isVideo: Boolean(img.is_video),
      isPrimary: Boolean(img.is_primary),
      variantId: img.variant_id ?? null,
      customOptionId: img.custom_option_id ?? null,
    }))
    .filter((m) => Boolean(m.url));

  const primaryIdx = media.findIndex((m) => m.isPrimary);
  if (primaryIdx > 0) {
    const [primary] = media.splice(primaryIdx, 1);
    media.unshift(primary);
  }

  const images = media.map((m) => m.url);

  const variants = [
    ...new Set(
      (p.product_variants || []).map((v) => v.variant_value).filter(Boolean)
    ),
  ];

  const discount = base > sale ? Math.round(((base - sale) / base) * 100) : 0;

  const primaryCat = (p.categories || []).find(c => c.ProductCategory?.is_primary) || p.categories?.[0] || p.category;

  return {
    id: p.id,
    name: p.title,
    description: p.description || '',
    short_description: p.short_description || '',
    shipping_info: p.shipping_info || '',
    return_info: p.return_info || '',
    shop_us: p.shop_us || '',
    warning_text: p.warning_text || '',
    category: primaryCat?.slug || '',
    categoryName: primaryCat?.name || '',
    categoryIds: (p.categories || []).map(c => c.id),
    categorySlugs: (p.categories || []).map(c => c.slug),
    categoryPath: primaryCat,
    price: sale,
    originalPrice: base,
    discount,
    images: images.length
      ? images
      : ['https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=600&q=80'],
    media: media.length
      ? media
      : [{ url: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=600&q=80', isVideo: false, isPrimary: true }],
    variants: variants.length ? variants : ['Default'],
    product_variants: p.product_variants || [],
    customization_fields: p.customization_fields || [],
    addons: p.addons || [],
    rating: 4.5,
    reviews: Math.floor(50 + Math.random() * 200),
    tag: p.is_featured ? 'Bestseller' : p.is_new_arrival ? 'New' : null,
    inStock: (p.stock ?? 1) > 0,
    isFeatured: Boolean(p.is_featured),
    isNewArrival: Boolean(p.is_new_arrival),
    isActive: Boolean(p.is_active),
    isBulk: Boolean(p.is_bulk),
    slug: p.slug,
    sku: p.sku,
  };
};

// ── Delivery Types ─────────────────────────────────────────
export const fetchDeliveryTypes = async () => {
  const { data } = await api.get('/delivery-types');
  return Array.isArray(data) ? data : (data.data ?? []);
};

// ── Helpers: normalise API delivery type ──
export const normaliseDeliveryType = (d) => ({
  id: d.id,
  name: d.name || '',
  description: d.description || '',
  estimatedDays: d.estimated_days || '',
  charge: parseFloat(d.price ?? d.charge ?? 0),
  isActive: d.is_active !== false,
});

export const normaliseCategory = (c) => ({
  id: c.slug,
  rawId: c.id,
  name: c.name,
  slug: c.slug,
  isActive: Boolean(c.is_active),
  imageUrl: c.image_url || '',
});

// ── Checkout ───────────────────────────────────────────────
export const submitOrder = async (payload) => {
  const { data } = await api.post('/checkout', payload);
  return data.data ?? data;
};

// ── Helpers: normalise API order ──
export const normaliseOrder = (o) => ({
  id:             o.id,
  customerId:     o.customer_id,
  addressId:      o.address_id,
  deliveryTypeId: o.delivery_type_id,
  deliveryCharge: parseFloat(o.delivery_charge ?? 0),
  subtotal:       parseFloat(o.subtotal ?? 0),
  totalAmount:    parseFloat(o.total_amount ?? 0),
  status:         o.status || 'pending',
  createdAt:      o.createdAt || null,
  // Razorpay fields
  razorpay_order_id: o.razorpay_order_id || null,
  amount:            o.amount ?? null,
  currency:          o.currency || 'INR',
  customer:          o.customer || null,
});

// ── Customer Auth ──────────────────────────────────────────

export const sendOtp = async (email) => {
  const { data } = await api.post('/auth/send-otp', { email });
  return data.data ?? data;
};

export const verifyOtp = async (email, otp) => {
  const { data } = await api.post('/auth/verify-otp', { email, otp });
  return data.data ?? data;
};

export const normaliseCustomer = (c) => ({
  id:        c.id,
  firstName: c.first_name,
  lastName:  c.last_name,
  email:     c.email,
  phone:     c.phone,
  fullName:  `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
});

// ── Customer Profile ───────────────────────────────────────

/**
 * GET /api/customer/:id
 * Fetch full customer details by ID
 */
export const fetchCustomer = async (id) => {
  const { data } = await api.get(`/customer/${id}`);
  return data.data?.customer ?? data.data ?? data;
};

/**
 * PUT /api/customer/:id
 * Update customer profile — only first_name, last_name, phone are editable
 * Email is read-only (controlled by auth)
 */
export const updateCustomer = async ({ id, first_name, last_name, phone }) => {
  const { data } = await api.put(`/customer/${id}`, { first_name, last_name, phone });
  return data.data?.customer ?? data.data ?? data;
};

// ── Customer Orders ────────────────────────────────────────

export const fetchCustomerOrders = async (customerId) => {
  const { data } = await api.get(`/customer/${customerId}/orders`);
  return data;
};

export const fetchCustomerOrderById = async (customerId, orderId) => {
  const { data } = await api.get(`/customer/${customerId}/orders/${orderId}`);
  return data;
};

// ── Banners ────────────────────────────────────────────────
export const fetchBanners = async () => {
  const { data } = await api.get('/banner', { params: { is_active: true } });
  return Array.isArray(data) ? data : (data.data ?? []);
};

export const normaliseBanner = (b) => ({
  id:         b.id,
  title:      b.title || '',
  image_url:  b.image_url || '',
  mobile_image_url:  b.mobile_image_url || '',
  sort_order: b.sort_order ?? 0,
  is_active:  b.is_active !== false,
  redirect_url:  b.redirect_url || '',
});

// ── Clients ────────────────────────────────────────────────

/**
 * GET /api/client
 * Returns list of trusted client logos (active only).
 * Expected response shape: { data: [...] } or [...].
 */
export const fetchClients = async () => {
  const { data } = await api.get('/client');
  return Array.isArray(data) ? data : (data.data ?? []);
};

/**
 * Normalise a raw client record into a component-friendly shape.
 * Adjust field names below if your API uses different keys.
 */
export const normaliseClient = (c) => ({
  id:        c.id,
  name:      c.name || c.title || '',
  imageUrl:  c.image_url || c.logo_url || c.imageUrl || '',
  sortOrder: c.sort_order ?? c.sortOrder ?? 0,
  isActive:  c.is_active !== false,
});

// ── Reels ────────────────────────────────────────────────

/**
 * GET /api/reel
 * Returns list of trusted reel (active only).
 * Expected response shape: { data: [...] } or [...].
 */
export const fetchReels = async () => {
  const { data } = await api.get('/reel');
  return Array.isArray(data) ? data : (data.data ?? []);
};

/**
 * Normalise a raw client record into a component-friendly shape.
 * Adjust field names below if your API uses different keys.
 */
export const normaliseReel = (c) => ({
  id:        c.id,
  name:      c.name || c.title || '',
  videoUrl:  c.video_url || c.logo_url || c.videoUrl || '',
  sortOrder: c.sort_order ?? c.sortOrder ?? 0,
  isActive:  c.is_active !== false,
});

// ── Verify Razorpay Payment ────────────────────────────────
export const verifyPayment = async (payload) => {
  const { data } = await api.post('/verify-payment', payload);
  return data.data ?? data;
};

// ── Mark Payment Failed ────────────────────────────────────
export const markPaymentFailed = async (payload) => {
  const { data } = await api.post('/failed', payload);
  return data.data ?? data;
};

export const fetchCategoryTree = async () => {
  const { data } = await api.get('/categories/tree');
  return data.data ?? [];
};

// ── Analytics ──────────────────────────────────────────────
export const startAnalyticsSession = async (payload) => {
  const { data } = await api.post('/analytics/session/start', payload);
  return data.data ?? data;
};

export const trackAnalyticsEvent = async (payload) => {
  const { data } = await api.post('/analytics/event', payload);
  return data.data ?? data;
};

export const endAnalyticsSession = async (payload) => {
  const { data } = await api.post('/analytics/session/end', payload);
  return data.data ?? data;
};